// ─── src/hooks/useCatalystPortal.ts ───────────────────────────────────────────

import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  catalystCheckInById,
  catalystCheckInByToken,
  catalystUndoCheckIn,
  getCatalystEventAttendees,
  getCatalystEvents,
  getCatalystProposals,
  updateCatalystAttendeeApproval,
  updateCatalystEvent,
  updateCatalystProposal,
  type UpdateCatalystEventPayload,
  type UpdateCatalystProposalPayload,
} from "../api/catalystPortal";

export const catalystKeys = {
  all:        ["catalyst"] as const,
  proposals:  () => [...catalystKeys.all, "proposals"] as const,
  events:     () => [...catalystKeys.all, "events"] as const,
  attendees:  (eventId: string, filters: { status?: string; approvalStatus?: string } = {}) =>
    [...catalystKeys.all, "attendees", eventId, filters] as const,
};

export const useCatalystProposals = () =>
  useQuery({
    queryKey:  catalystKeys.proposals(),
    queryFn:   getCatalystProposals,
    staleTime: 30_000,
  });

export const useCatalystEvents = () =>
  useQuery({
    queryKey:  catalystKeys.events(),
    queryFn:   getCatalystEvents,
    staleTime: 30_000,
  });

export const useCatalystEventAttendees = (
  eventId: string,
  filters: { status?: string; approvalStatus?: string } = {},
  enabled = true,
) =>
  useQuery({
    queryKey:        catalystKeys.attendees(eventId, filters),
    queryFn:         () => getCatalystEventAttendees(eventId, filters),
    placeholderData: keepPreviousData,
    staleTime:       15_000,
    enabled,
  });

// ── Mutations ────────────────────────────────────────────────────────────────

export const useCatalystApproveAttendee = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, accepted }: { id: string; accepted: boolean }) =>
      updateCatalystAttendeeApproval(id, accepted),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalystKeys.all });
    },
  });
};

export const useCatalystCheckInByToken = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ token, eventId }: { token: string; eventId?: string }) =>
      catalystCheckInByToken(token, eventId),
    onSuccess: () => qc.invalidateQueries({ queryKey: catalystKeys.all }),
  });
};

export const useCatalystCheckInById = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalystCheckInById(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: catalystKeys.all }),
  });
};

export const useCatalystUndoCheckIn = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalystUndoCheckIn(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: catalystKeys.all }),
  });
};

export const useCatalystUpdateEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, payload }: { eventId: string; payload: UpdateCatalystEventPayload }) =>
      updateCatalystEvent(eventId, payload),
    // Refetch events (capacity/counts may shift) and attendees (waitlist may
    // have promoted after a capacity bump).
    onSuccess: () => qc.invalidateQueries({ queryKey: catalystKeys.all }),
  });
};

/**
 * Catalyst edit for their own pending proposal.
 * Backend rejects the PATCH once the proposal transitions to
 * accepted/rejected — the UI hides the button too, but this is the guard
 * that actually matters.
 */
export const useCatalystUpdateProposal = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ proposalId, payload }: { proposalId: string; payload: UpdateCatalystProposalPayload }) =>
      updateCatalystProposal(proposalId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: catalystKeys.all }),
  });
};
